import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../../../components/ui/Button/Button";
import { analyticsWebsites } from "../../../api/analyticsWebsites";
import { analyticsKey, type AnalyticsScope } from "../queryKeys";
import type { ContextResponse, DateRange } from "../types";
import { dateLabel, rangeParams } from "../dateRange";
import { errorMessage } from "../format";
import { Loading } from "./States";
import styles from "../Analytics.module.css";
export default function InsightsPanel({
  scope,
  id,
  range,
  context,
}: {
  scope: AnalyticsScope;
  id: string;
  range: DateRange;
  context: ContextResponse;
}) {
  const client = useQueryClient();
  const busy = useRef(false);
  const [showAll, setShowAll] = useState(false);
  const request = useRef<{ context: string; id: string } | null>(null);
  const key = analyticsKey(
    scope,
    "insights",
    id,
    context.context.mapping_version,
    context.context.dataset_version,
    range,
  );
  const query = useQuery({
    queryKey: key,
    queryFn: async ({ signal }) => {
      const response = await analyticsWebsites.insights(id, range, signal);
      if (
        response.context.website_id !== id ||
        response.context.mapping_version !== context.context.mapping_version ||
        response.context.dataset_version !== context.context.dataset_version
      ) {
        void client.invalidateQueries({
          queryKey: analyticsKey(scope, "context", id),
        });
        throw new Error(
          "The report changed while loading. Its latest period and connections are being checked.",
        );
      }
      return response;
    },
    staleTime: 30000,
  });
  const explain = useMutation({
    mutationFn: (snapshot: {
      id: string;
      range: DateRange;
      version: number | string;
      mappingVersion: number;
      requestId: string;
      key: readonly unknown[];
    }) =>
      analyticsWebsites.explain(
        snapshot.id,
        snapshot.range,
        snapshot.version,
        snapshot.requestId,
      ),
    onSuccess: (data, snapshot) => {
      if (
        data.context.website_id === snapshot.id &&
        data.context.mapping_version === snapshot.mappingVersion &&
        data.context.dataset_version === snapshot.version
      )
        client.setQueryData(snapshot.key, data);
      else
        void client.invalidateQueries({
          queryKey: analyticsKey(scope, "context", snapshot.id),
        });
      if (request.current?.id === snapshot.requestId) request.current = null;
    },
    onError: (error, snapshot) => {
      const responseError = error as {
        status?: number;
        detail?: { code?: string };
      };
      const code = responseError?.detail?.code;
      // A structured terminal response establishes that a new explicit action is needed.
      // Unknown transport outcomes keep the same ID to avoid duplicate paid generation.
      if (
        responseError.status !== undefined &&
        responseError.status >= 400 &&
        responseError.status <= 599 &&
        code &&
        [
          "generation_failed",
          "invalid_explanation",
          "generation_rate_limited",
          "generation_disabled",
          "explanation_interrupted",
        ].includes(code) &&
        request.current?.id === snapshot.requestId
      )
        request.current = null;
    },
    onSettled: () => {
      busy.current = false;
    },
  });
  const generate = () => {
    if (busy.current) return;
    busy.current = true;
    const identity = JSON.stringify(key);
    if (request.current?.context !== identity)
      request.current = { context: identity, id: crypto.randomUUID() };
    explain.mutate({
      id,
      range: { ...range },
      version: context.context.dataset_version,
      mappingVersion: context.context.mapping_version,
      requestId: request.current.id,
      key,
    });
  };
  const data =
    query.data?.context.website_id === id &&
    query.data.context.mapping_version === context.context.mapping_version &&
    query.data.context.dataset_version === context.context.dataset_version
      ? query.data.payload
      : undefined;
  return (
    <section className={styles.panel}>
      <div className={styles.panelHeading}>
        <div>
          <h2>
            <Sparkles size={18} />
            Understand the changes
          </h2>
          <p>
            {dateLabel(range.start)} – {dateLabel(range.end)} · Selected sources
            only
          </p>
        </div>
        <Button
          variant="secondary"
          icon={<Sparkles size={15} />}
          disabled={!data?.can_explain || query.isPending}
          loading={explain.isPending}
          onClick={generate}
        >
          Explain changes
        </Button>
      </div>
      <p className={styles.caption}>
        Explanations use report evidence. Generate one only when you need it;
        your account’s AI usage limits apply.
      </p>
      {query.isPending ? (
        <Loading label="Loading observations…" />
      ) : query.isError ? (
        <p role="alert">
          Observations could not be loaded.{" "}
          <button className={styles.textLink} onClick={() => query.refetch()}>
            Try again
          </button>
        </p>
      ) : !data?.items.length ? (
        <p className={styles.insightEmpty}>
          {data?.can_explain
            ? "No observations for this period. Use Explain changes to request an explanation."
            : "More reliable report evidence is needed before changes can be explained. Refresh data or select a period with coverage."}
        </p>
      ) : (
        <div className={styles.insights}>
          {(showAll ? data.items : data.items.slice(0, 3)).map((item) => (
            <article key={item.id} className={styles.insight}>
              <div>
                <strong>{item.title}</strong>
                {item.outdated && (
                  <span className={styles.badge}>Outdated</span>
                )}
              </div>
              <p>{item.description}</p>
              <small>
                {item.source} · {dateLabel(item.period_start)} –{" "}
                {dateLabel(item.period_end)} · Updated{" "}
                {new Date(item.generated_at).toLocaleString()}
              </small>
              {item.evidence_ids.length > 0 && (
                <details>
                  <summary>Report evidence</summary>
                  <ul>
                    {item.evidence_ids.map((e) => (
                      <li key={e}>{e}</li>
                    ))}
                  </ul>
                  <Link
                    className={styles.textLink}
                    to={`/analytics/websites/${id}/${item.source === "ga4" ? "traffic" : "search"}?${rangeParams({ ...range, start: item.period_start, end: item.period_end })}`}
                  >
                    Inspect source report →
                  </Link>
                </details>
              )}
              {item.affected_pages.length > 0 && (
                <details>
                  <summary>Affected pages</summary>
                  <ul>
                    {item.affected_pages.map((page) => (
                      <li key={page}>
                        <Link
                          to={`/analytics/websites/${id}/pages?${rangeParams({ ...range, start: item.period_start, end: item.period_end })}&q=${encodeURIComponent(page)}`}
                        >
                          {page}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </article>
          ))}
          {data.items.length > 3 && (
            <Button
              variant="ghost"
              onClick={() => setShowAll(!showAll)}
              aria-expanded={showAll}
            >
              {showAll
                ? "Show fewer"
                : `Show all ${data.items.length} observations`}
            </Button>
          )}
        </div>
      )}
      {data?.limitations?.map((v, i) => (
        <p key={i} className={styles.caption}>
          {v}
        </p>
      ))}
      {explain.isError && (
        <p role="alert">
          {errorMessage(explain.error)}{" "}
          <button className={styles.textLink} onClick={generate}>
            Try again
          </button>
        </p>
      )}
    </section>
  );
}
